import React, { useContext, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import CustomSelect from "@shared/components/CustomSelect";
import Loader from "@shared/components/Loader";
import { BrandingContext } from "@shared/branding/BrandingContext";
import {
  isPushSupported,
  notificationPermission,
  fetchServerState,
  getBrowserSubscription,
  subscribe,
  updateSettings,
  unsubscribe,
} from "@shared/push/pushService";
import {
  isNativePushAvailable,
  nativePushReady,
  needsAppUpdateForPush,
  nativePermission,
  registeredToken,
  enableNativePush,
  disableNativePush,
} from "@shared/push/nativePush";
import {
  canPromptInstall,
  promptInstall,
  onInstallableChange,
  isStandalone,
  isNativeApp,
  isIOS,
  isIOSSafari,
} from "@shared/push/installPrompt";
import "./Preferences.css";
import "./Notifications.css";

const HOURS = Array.from({ length: 17 }, (_, index) => {
  const hour = index + 6; // 06:00 … 22:00
  return { value: String(hour), label: `${String(hour).padStart(2, "0")}:00` };
});

/**
 * Einstellungen → Benachrichtigungen: reminders for this device and, since
 * iOS only allows them for an installed web app, the way to install it. In
 * the store app the device registers through the native plugin instead
 * (nativePush.js); settings and texts are the same.
 */
export default function Notifications() {
  const { t } = useTranslation();
  const { brandName } = useContext(BrandingContext);
  const app = brandName || "sophyapp";
  // Settled by the first refresh, once the native app version is known.
  const [native, setNative] = useState(isNativePushAvailable());
  const supported = native || isPushSupported();
  const standalone = isStandalone();

  const [server, setServer] = useState({ loading: true, data: null, error: false });
  // This device's key in the backend: the browser's push endpoint, or the FCM token.
  const [deviceEndpoint, setDeviceEndpoint] = useState(null);
  const [permission, setPermission] = useState(native ? "default" : notificationPermission());
  const [reminderHour, setReminderHour] = useState("18");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [installable, setInstallable] = useState(canPromptInstall());

  const refresh = async () => {
    await nativePushReady();
    const isNative = isNativePushAvailable();
    setNative(isNative);
    try {
      const data = await fetchServerState();
      setServer({ loading: false, data, error: false });
    } catch (error) {
      setServer({ loading: false, data: null, error: true });
    }
    if (isNative) {
      setDeviceEndpoint(registeredToken());
      try {
        setPermission(await nativePermission());
      } catch (error) {
        setPermission("default");
      }
      return;
    }
    try {
      setDeviceEndpoint((await getBrowserSubscription())?.endpoint || null);
    } catch (error) {
      setDeviceEndpoint(null);
    }
    setPermission(notificationPermission());
  };

  useEffect(() => {
    refresh();
    return onInstallableChange(setInstallable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const device =
    (deviceEndpoint && server.data?.subscriptions?.find((entry) => entry.endpoint === deviceEndpoint)) || null;

  useEffect(() => {
    if (device?.reminder_hour !== undefined && device?.reminder_hour !== null) {
      setReminderHour(String(device.reminder_hour));
    }
  }, [device]);

  const active = Boolean(device && device.reminders_enabled && permission === "granted");
  // Native push needs the backend's FCM credentials, which an older backend does not report.
  const configured = native ? server.data?.native_configured === true : server.data?.configured !== false;

  const enable = async () => {
    setBusy(true);
    setMessage(null);
    try {
      if (native) {
        await enableNativePush({ reminderHour: Number(reminderHour) });
      } else {
        await subscribe(server.data?.vapid_public_key, { reminderHour: Number(reminderHour) });
      }
      await refresh();
      setMessage({ type: "success", text: t("push.saved") });
    } catch (error) {
      if (native) {
        setPermission(await nativePermission().catch(() => "default"));
      } else {
        setPermission(notificationPermission());
      }
      const denied = native ? t("push.denied_native", { app }) : t("push.denied");
      setMessage({ type: "danger", text: error?.code === "denied" ? denied : t("push.error") });
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await (native ? disableNativePush() : unsubscribe());
      await refresh();
      setMessage({ type: "success", text: t("push.saved") });
    } catch (error) {
      setMessage({ type: "danger", text: t("push.error") });
    } finally {
      setBusy(false);
    }
  };

  const changeHour = async (value) => {
    setReminderHour(value);
    if (!device) return;
    try {
      await updateSettings(device.endpoint, { reminderHour: Number(value), enabled: true });
      setMessage({ type: "success", text: t("push.saved") });
    } catch (error) {
      setMessage({ type: "danger", text: t("push.error") });
    }
  };

  const status = (() => {
    if (!supported) {
      // A store build without the push plugin, or a platform that has none yet.
      if (isNativeApp()) {
        return needsAppUpdateForPush() ? t("push.status_native_update", { app }) : t("push.status_native");
      }
      return isIOS() && !standalone
        ? t("push.status_ios_install", { app })
        : t("push.status_unsupported");
    }
    if (!configured) return t("push.status_not_configured");
    if (permission === "denied") return native ? t("push.status_denied_native", { app }) : t("push.status_denied");
    if (active) return t("push.status_active", { hour: HOURS.find((h) => h.value === reminderHour)?.label || reminderHour });
    return t("push.status_inactive");
  })();

  const canEnable = supported && configured && permission !== "denied" && !server.error;

  if (server.loading) return <Loader />;

  return (
    <Container fluid className="h-100 notif">
      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
          {message.text}
        </Alert>
      )}

      <h5 className="notif-section">{t("push.reminders")}</h5>

      <Row className="h-100 table-row">
        <Col md={3} className="table-column-title">
          {t("push.status")}
        </Col>
        <Col md={7}>
          <p className={`notif-status${active ? " notif-status-active" : ""}`}>{status}</p>
        </Col>
      </Row>

      <Row className="h-100 table-row">
        <Col md={3} className="table-column-title">
          {t("push.time")}
        </Col>
        <Col md={5}>
          <CustomSelect value={reminderHour} onChange={changeHour} options={HOURS} disabled={!canEnable} />
        </Col>
      </Row>

      <Row className="h-100 table-row">
        <Col md={3} className="table-column-title"></Col>
        <Col md={7}>
          {active ? (
            <button type="button" className="notif-btn" onClick={disable} disabled={busy}>
              {t("push.disable")}
            </button>
          ) : (
            <button type="button" className="notif-btn notif-btn-primary" onClick={enable} disabled={busy || !canEnable}>
              {t("push.enable")}
            </button>
          )}
          <p className="notif-hint">{t("push.how")}</p>
        </Col>
      </Row>

      {/* The store app is installed by definition. */}
      {!isNativeApp() && (
        <>
          <h5 className="notif-section">{t("push.install")}</h5>

          <Row className="h-100 table-row">
            <Col md={3} className="table-column-title"></Col>
            <Col md={7}>
              {standalone ? (
                <p className="notif-status notif-status-active">{t("push.install_done", { app })}</p>
              ) : (
                <>
                  <p className="notif-hint">{t("push.install_why", { app })}</p>
                  {installable ? (
                    <button type="button" className="notif-btn notif-btn-primary" onClick={() => promptInstall()}>
                      {t("push.install_button")}
                    </button>
                  ) : isIOSSafari() ? (
                    <ol className="notif-steps">
                      <li>{t("push.install_ios_1")}</li>
                      <li>{t("push.install_ios_2")}</li>
                      <li>{t("push.install_ios_3", { app })}</li>
                    </ol>
                  ) : (
                    <p className="notif-status">{t("push.install_other")}</p>
                  )}
                </>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
